import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountView } from '@api-proxies';
import { AccountService } from '@http/clients/account.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { TabCounterService, TabCounterType } from '@services/tab-counter.service';
import { ActivityLogGridComponent } from '@shared/activity-log-grid/activity-log-grid.component';
import { KebabMenuComponent } from '@shared/kebab-menu/kebab-menu.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { NotesComponent } from '@shared/notes/notes.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { getTab, processTabParameter } from '@utils/tab-navigation';
import {
    CounterBadgeComponent,
    ModalService,
    StatusIndicatorComponent,
    TabItemComponent,
    TabViewComponent
} from 'ngx-lib';
import { Subscription } from 'rxjs';
import { AccountsSingleBuildingsComponent } from './accounts-single-buildings/accounts-single-buildings.component';
import { AccountsSingleCompaniesComponent } from './accounts-single-companies/accounts-single-companies.component';
import { AccountsSingleContactsComponent } from './accounts-single-contacts/accounts-single-contacts.component';
import { AccountsSingleOpportunitiesComponent } from './accounts-single-opportunities/accounts-single-opportunities.component';
import { AccountsSingleOverviewComponent } from './accounts-single-overview/accounts-single-overview.component';

@Component({
    selector: 'app-accounts-single',
    templateUrl: './accounts-single.component.html',
    styleUrls: ['./accounts-single.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SectionHeaderComponent,
        StatusIndicatorComponent,
        TabViewComponent,
        TabItemComponent,
        CounterBadgeComponent,
        KebabMenuComponent,
        AccountsSingleOverviewComponent,
        AccountsSingleCompaniesComponent,
        AccountsSingleOpportunitiesComponent,
        AccountsSingleContactsComponent,
        AccountsSingleBuildingsComponent,
        NotesComponent,
        ActivityLogGridComponent
    ]
})
export class AccountsSingleComponent implements OnInit, OnDestroy {
    @ViewChild(TabViewComponent)
    tabControl!: TabViewComponent;

    accountId: number;

    account?: AccountView;

    currentTab: string;

    listViewSelectedItem?: AccountView;

    isActive?: boolean;

    entityTypeId: EntityTypes;

    entitySubscription?: Subscription;

    tabsCounters: TabCounterType = {
        companies: 0,
        Opportunities: 0,
        Contacts: 0,
        Buildings: 0,
        Notes: 0
    };

    constructor(
        private readonly accountService: AccountService,
        private readonly awaiter: AwaiterService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly modalService: ModalService,
        private readonly tabCounterService: TabCounterService
    ) {
        this.accountId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.currentTab = getTab(this.route.snapshot.paramMap.get('tab')) ?? TabNames[TabNames.Overview];
        this.isActive = true;
        this.entityTypeId = EntityTypes.Account;
    }

    async ngOnInit(): Promise<void> {
        await this.updateTabCounters();

        this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
            this.account = entityData;
            this.isActive = this.account?.isActive;
        });

        this.route.paramMap.subscribe(async (params) => {
            const newTab = getTab(params.get('tab'));

            if (newTab && newTab !== this.currentTab && this.tabControl) {
                this.currentTab = processTabParameter(this.route, this.tabControl);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    onEditAccount(): void {
        this.router.navigate([`/accounts-edit/${this.accountId}`]);
    }

    async archiveAccount(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Archive',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to archive this account?',
            title: 'Archive Account'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Archiving Account', async () => {
            if (!this.account?.id) return;

            await this.accountService.deactivate(this.account.id);
            this.isActive = false;
        });
    }

    async restoreAccount(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Restore',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to restore this account?',
            title: 'Restore Account'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Restoring Account', async () => {
            if (!this.account?.id) return;
            await this.accountService.activate(this.account.id);
            this.isActive = true;
        });
    }

    async onTabCountersChange(): Promise<void> {
        await this.updateTabCounters();
    }

    tabChange(tabName: string) {
        this.router.navigate(['/accounts-single', this.accountId, tabName]);
    }

    async handleAccountChange(): Promise<void> {
        if (!this.accountId) return;

        const res = await this.updateAccountInfo();
        if (res) await this.updateTabCounters();
    }

    private async updateAccountInfo(): Promise<boolean | undefined> {
        return await this.awaiter.awaitAction('Getting Account Info', async () => {
            this.account = await this.accountService.getById(this.accountId);
            this.isActive = this.account.isActive;
            return true;
        });
    }

    private async updateTabCounters(): Promise<void> {
        await this.tabCounterService.updateCounters(this.entityTypeId, this.accountId, this.tabsCounters);
    }
}
